// =====================================================================
// Should THIS tab answer an inbound call?
//
// The bridge TwiML dials `<Client><Identity>sdr_N</Identity>`, which rings EVERY
// endpoint registered for that identity, so every cockpit tab an SDR has open competes
// to answer. The BFF only pushes the card to the session whose pool matches the call.
// A tab that wins the race while holding no seat therefore takes media it can never
// card — and because the blip mute is released only by a card, it holds the leg muted
// to the safety cap with the mic open. The SDR sees a card in the other tab and hears
// silence; the lead hears an open office and hangs up.
//
// The measure is how long this tab has been WITHOUT A SEAT, not how long it has been
// on the picker. A tab whose socket dropped sits in `connecting`, which is every bit
// as uncardable as the picker and is unbounded in time — and measuring from the last
// socket event rather than from idleness would hand a forgotten tab a fresh grace
// window on every reconnect, which a flapping link supplies indefinitely.
//
// AMBIGUITY ANSWERS. Wrongly answering is exactly today's behaviour; wrongly refusing
// can ring a real contact out to the dial timeout. Hence the grace window: a socket
// drop clears `activePoolId` before the session snapshot restores it, and during that
// window this tab may still be the SDR's real seat.
// =====================================================================

import type { CockpitPhase } from './state';

// Phases in which this tab holds, or is taking, a seat in a pool — the card can land
// here, so the call is ours to answer.
const SEATED: ReadonlySet<CockpitPhase> = new Set<CockpitPhase>([
  'joining',
  'waiting',
  'on_call',
  'ending',
]);

// How long a tab may be seatless and still answer. It has to outlast a reconnect that
// is genuinely going to restore a seat. `useCockpit` gives the snapshot up to
// RESTORE_TIMEOUT_MS (15s), but ONLY on the held path — that deadline is armed just for
// a deploy nudge, and an ordinary socket drop restores against no bound at all. So this
// is sized generously rather than derived from it.
//
// The asymmetry decides the number. Too long and a forgotten tab stays answerable a
// little longer, which is exactly today's behaviour. Too short and a tab that WOULD
// have been restored declines instead, and if it is the SDR's only endpoint a live
// contact rings out — a call lost rather than a call degraded. Minutes-old tabs are
// what this is for, so buying margin here costs nothing worth having.
export const UNSEATED_GRACE_MS = 45_000;

export type InboundReason =
  | 'seated' // answer: this tab has, or is taking, a seat
  | 'unstamped' // answer: never held a seat to lose (fresh tab), so nothing to measure
  | 'unseated_grace' // answer: seatless, but recently enough to be a restore in flight
  | 'unseated_idle'; // ignore: seatless long enough that a card can never land here

export interface InboundDecision {
  answer: boolean;
  reason: InboundReason;
  // Reported, not decided on: `seated` collapses four phases, and an answer from
  // `connecting` (socket down, no card possible) reads very differently from one in
  // `waiting`.
  phase: CockpitPhase;
  // How long this tab has been seatless — the ignore-vs-ring-out question.
  unseatedMs: number | null;
}

export function decideInbound(ctx: {
  phase: CockpitPhase;
  // Epoch ms this tab last lost its seat, or null if it holds one (or never had one).
  unseatedSince: number | null;
  now: number;
}): InboundDecision {
  const { phase, unseatedSince, now } = ctx;
  // Checked ahead of the stamp so a bookkeeping slip upstream can never refuse a call
  // to a tab that is demonstrably seated.
  if (SEATED.has(phase)) return { answer: true, reason: 'seated', phase, unseatedMs: null };
  if (unseatedSince == null) return { answer: true, reason: 'unstamped', phase, unseatedMs: null };
  const unseatedMs = Math.max(0, now - unseatedSince);
  if (unseatedMs < UNSEATED_GRACE_MS) {
    return { answer: true, reason: 'unseated_grace', phase, unseatedMs };
  }
  return { answer: false, reason: 'unseated_idle', phase, unseatedMs };
}
